/**
 * Mirrors GraphQL `UserRole` (SCREAMING_SNAKE).
 * `USER_ROLE_INT` holds DB ints — use `parseGraphqlEnumName(value, USER_ROLE_INT)` when parsing JWT/API.
 */
import {
  graphqlEnumNames,
  isGraphqlEnumName,
  parseGraphqlEnumName,
  type GraphqlEnumName,
} from "$lib/enums/graphql";

/** GraphQL / session role names (values equal keys for ergonomic `UserRole.BROKER` checks). */
export const UserRole = {
  ADMIN: "ADMIN",
  BROKER: "BROKER",
  EXTERNAL: "EXTERNAL",
  ASSISTANT: "ASSISTANT",
  TEAM_LEAD: "TEAM_LEAD",
  ORGANIZATION_ADMIN: "ORGANIZATION_ADMIN",
  FRANCHISE_MANAGER: "FRANCHISE_MANAGER",
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/** DB IntEnum values (`common.enums.UserRole`). Keys = GraphQL names. */
export const USER_ROLE_INT = {
  ADMIN: 1,
  BROKER: 2,
  EXTERNAL: 3,
  ASSISTANT: 4,
  TEAM_LEAD: 5,
  ORGANIZATION_ADMIN: 6,
  FRANCHISE_MANAGER: 7,
} as const satisfies Record<UserRole, number>;

export const USER_ROLE_VALUES = graphqlEnumNames(USER_ROLE_INT);

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === "string" && isGraphqlEnumName(value, USER_ROLE_INT);
}

export function parseUserRole(value: unknown): UserRole | null {
  const name = parseGraphqlEnumName(value, USER_ROLE_INT);
  return name && isUserRole(name) ? name : null;
}

export type UserRoleName = GraphqlEnumName<typeof USER_ROLE_INT>;

/**
 * Defense-in-depth chrome gates until BE ASSISTANT ACL is verified (P0-FE-6).
 * Never rely on these for authorization — BE remains authoritative.
 */
export function canMutateListings(role: UserRole | null | undefined): boolean {
  return role != null && role !== UserRole.ASSISTANT && role !== UserRole.EXTERNAL;
}

export function canCreateClients(role: UserRole | null | undefined): boolean {
  return role != null && role !== UserRole.ASSISTANT && role !== UserRole.EXTERNAL;
}

/** Leads create/delete chrome — same defense-in-depth as clients (P0-FE-6). */
export function canMutateLeads(role: UserRole | null | undefined): boolean {
  return canCreateClients(role);
}

export function canTopUpCredits(role: UserRole | null | undefined): boolean {
  return role != null && role !== UserRole.ASSISTANT;
}

export function canSeeUsersDirectory(role: UserRole | null | undefined): boolean {
  return role != null && role !== UserRole.ASSISTANT;
}
